// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';
import type { DropdownOption } from '../../NodeEditorDrawer/types';

import { ActionType } from 'flow/store/engine/constants';
import type { Action, PopulatedThreshold } from 'flow/store/engine/types';

import type { Attribute } from '~/flow/types';

import {
  DATE_TIMESTAMP_OPERATORS,
  DEFAULT_OPERATORS,
  DEFAULT_OPERATORS_NULL_ALLOWED,
  MATCHING_OPERATORS,
  OPERATORS,
  REASON_CODE_OPERATORS,
  STRING_OPERATORS,
  STRING_OPERATORS_NULL_ALLOWED,
} from './constants';
import { supportedNullActionTypes } from '../RuleThresholdDropdown/constants';


export function getOperatorsAsMultiSelectOptions(operatorKeys: string[]): Option[] {
  return operatorKeys.map(key => ({
    label: OPERATORS[key].symbol || OPERATORS[key].text,
    value: key,
    meta: OPERATORS[key].customKey ? {
      customKey: OPERATORS[key].customKey,
    } : {},
  }));
}

export function getThresholdOperatorString({
  action,
  attribute,
  operator,
}: {
  action: Action;
  attribute?: Attribute;
  operator: string;
}): string {
  if (action.type === ActionType.TAG || action.type === ActionType.OUTCOME_REASON) {
    return 'is';
  }
  if (attribute && attribute.dataType === 'reasoncodes') {
    return OPERATORS[`codes_${operator}`].text;
  }
  return OPERATORS[operator].symbol || OPERATORS[operator].text;
}

/**
 * This is pulled almost verbatim from the old editor as a means of determining which operators
 * to display as options based on the configuration of the existing threshold, usually dependent
 * on the datatype of the left attribute
 */
export function getOperatorsAsOptions({
  attribute,
  dataType,
  thresholdType,
  threshold,
  supportsBestEffortParsing = false,
}: {
  attribute: Attribute | null;
  dataType: string;
  thresholdType: 'static' | 'dynamic';
  threshold: PopulatedThreshold;
  supportsBestEffortParsing?: boolean;
}): DropdownOption[] {
  const attributeOptions = attribute && attribute.values ? attribute.values.split(',') : [];
  let dropdownOptions = [];
  switch (dataType) {
    case 'string': {
      // if no checkbox options provided for left action attribute
      if (!attributeOptions.length || !attributeOptions[0]) {
        if (supportsBestEffortParsing
          && supportedNullActionTypes.has(threshold.left.action.type)
          && thresholdType !== 'dynamic'
        ) {
          dropdownOptions = STRING_OPERATORS_NULL_ALLOWED;
          break;
        }
        dropdownOptions = STRING_OPERATORS;
      } else {
        dropdownOptions = thresholdType === 'dynamic' ? ['e'] : [];
      }
      break;
    }
    case 'reasoncodes': {
      dropdownOptions = REASON_CODE_OPERATORS;
      break;
    }
    case 'boolean':
      dropdownOptions = ['e'];
      break;
    case 'decimal':
    case 'integer': {
      if (thresholdType === 'dynamic') {
        dropdownOptions = MATCHING_OPERATORS;
        break;
      }
      if (threshold.left.action.type === ActionType.INPUT
        || (
          supportsBestEffortParsing
          && supportedNullActionTypes.has(threshold.left.action.type)
        )
      ) {
        dropdownOptions = DEFAULT_OPERATORS_NULL_ALLOWED;
        break;
      }
      dropdownOptions = DEFAULT_OPERATORS;
      break;
    }
    case 'date':
    case 'timestamp': {
      if (thresholdType === 'dynamic') {
        dropdownOptions = MATCHING_OPERATORS;
        break;
      }
      dropdownOptions = DATE_TIMESTAMP_OPERATORS;
      break;
    }
  }
  return getOperatorsAsMultiSelectOptions(dropdownOptions);
}
